import { useEffect, useRef } from 'react';

interface ShortcutMap {
  [key: string]: () => void;
}

export function useKeyboardShortcuts(shortcuts: ShortcutMap, enabled = true) {
  const lastActionRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      // antd Select / AutoComplete render a focusable div with role="combobox"
      // and aria-haspopup="listbox" — typing 1-8 inside an open Select used
      // to fire triage actions because the target wasn't INPUT/TEXTAREA.
      if (
        target.getAttribute?.('role') === 'combobox' ||
        target.getAttribute?.('aria-haspopup') === 'listbox' ||
        target.closest?.('[role="combobox"]')
      ) {
        return;
      }
      // Suppress when any antd Modal is open. The modal traps focus on its
      // mask layer but background hotkeys still fire (e.g. cheat-sheet visible
      // → operator presses "1" thinking it's a modal control → triages issue).
      //
      // Two checks: the antd v5 mask class AND any role="dialog" element that
      // is visible — covers Modal, Drawer, Popconfirm, and future antd
      // re-styling that may rename the mask class.
      if (document.querySelector('.ant-modal-root .ant-modal-mask:not(.ant-modal-mask-hidden)')) {
        return;
      }
      // antd v5 markup: an open Modal renders `.ant-modal-mask` (no
      // `-hidden` suffix), Drawer adds `.ant-drawer-open`, Popconfirm/Popover
      // mounts under `.ant-popover` and only adds `.ant-popover-hidden` when
      // it's about to close. So the right "is something dialog-like open"
      // check is the presence of any of these elements without their hidden
      // suffix. (The earlier `[aria-hidden="false"]` selector was effectively
      // dead — antd doesn't set aria-hidden on open dialogs.)
      if (
        document.querySelector(
          '.ant-drawer-open, .ant-popover:not(.ant-popover-hidden), .ant-modal-wrap'
        )
      ) {
        return;
      }

      const key = [
        e.ctrlKey ? 'Ctrl' : '',
        e.shiftKey ? 'Shift' : '',
        e.key,
      ].filter(Boolean).join('+');

      if (shortcuts[key]) {
        const now = Date.now();
        if (now - lastActionRef.current < 300) return;
        lastActionRef.current = now;
        e.preventDefault();
        shortcuts[key]();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcuts, enabled]);
}
